// ============ DYNAMIC PROGRAMMING ALGORITHMS ============

export const generateFibonacciMemoSteps = (n) => {
  const steps = [];
  const dp = new Array(n + 1).fill(null);
  dp[0] = 0;
  if (n >= 1) dp[1] = 1;

  steps.push({
    line: 3,
    description: `Initialize DP table of size ${n + 1}. Set dp[0]=0, dp[1]=1`,
    variables: { n },
    dpTable: [...dp],
    currentIndex: -1,
    dimensions: '1D'
  });

  for (let i = 2; i <= n; i++) {
    steps.push({
      line: 5,
      description: `Computing dp[${i}] = dp[${i - 1}] + dp[${i - 2}]`,
      variables: { i, prev1: dp[i - 1], prev2: dp[i - 2] },
      dpTable: [...dp],
      currentIndex: i,
      dependencies: [i - 1, i - 2],
      dimensions: '1D'
    });

    dp[i] = dp[i - 1] + dp[i - 2];

    steps.push({
      line: 6,
      description: `dp[${i}] = ${dp[i - 1]} + ${dp[i - 2]} = ${dp[i]}`,
      variables: { i, value: dp[i] },
      dpTable: [...dp],
      currentIndex: i,
      dimensions: '1D'
    });
  }

  steps.push({
    line: 8,
    description: `Complete! fibonacci(${n}) = ${dp[n]}`,
    variables: { result: dp[n] },
    dpTable: [...dp],
    currentIndex: n,
    dimensions: '1D',
    result: dp[n]
  });

  return steps;
};

export const generateLCSSteps = (input) => {
  // Parse input like "ABCBDAB|BDCAB"
  const parts = input.split('|').map(s => s.trim());
  const s1 = parts[0] || 'ABCBDAB';
  const s2 = parts[1] || 'BDCAB';
  const m = s1.length;
  const n = s2.length;

  const steps = [];
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  steps.push({
    line: 3,
    description: `LCS of "${s1}" and "${s2}". Build ${m + 1}x${n + 1} DP table.`,
    variables: { m, n, s1, s2 },
    dpTable2D: dp.map(r => [...r]),
    s1,
    s2,
    dimensions: '2D',
    currentCell: null
  });

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const charI = s1[i - 1];
      const charJ = s2[j - 1];

      steps.push({
        line: 7,
        description: `Compare s1[${i - 1}]='${charI}' with s2[${j - 1}]='${charJ}'`,
        variables: { i, j, char1: charI, char2: charJ },
        dpTable2D: dp.map(r => [...r]),
        s1,
        s2,
        currentCell: [i, j],
        dimensions: '2D'
      });

      if (charI === charJ) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
        steps.push({
          line: 8,
          description: `Match! dp[${i}][${j}] = dp[${i - 1}][${j - 1}] + 1 = ${dp[i][j]}`,
          variables: { i, j, value: dp[i][j] },
          dpTable2D: dp.map(r => [...r]),
          s1,
          s2,
          currentCell: [i, j],
          dependencies: [[i - 1, j - 1]],
          dimensions: '2D',
          matched: true
        });
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        steps.push({
          line: 10,
          description: `No match. dp[${i}][${j}] = max(dp[${i - 1}][${j}], dp[${i}][${j - 1}]) = ${dp[i][j]}`,
          variables: { i, j, value: dp[i][j] },
          dpTable2D: dp.map(r => [...r]),
          s1,
          s2,
          currentCell: [i, j],
          dependencies: [[i - 1, j], [i, j - 1]],
          dimensions: '2D'
        });
      }
    }
  }

  steps.push({
    line: 14,
    description: `Complete! LCS length = ${dp[m][n]}`,
    variables: { result: dp[m][n] },
    dpTable2D: dp.map(r => [...r]),
    s1,
    s2,
    currentCell: [m, n],
    dimensions: '2D',
    result: dp[m][n]
  });

  return steps;
};

export const generateKnapsackSteps = (input) => {
  // Parse input like "1,3,4,5|1,4,5,7|7" (weights|values|capacity)
  const parts = input.split('|').map(s => s.trim());
  const weights = (parts[0] || '1,3,4,5').split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
  const values = (parts[1] || '1,4,5,7').split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
  const capacity = parseInt(parts[2]) || 7;
  const n = weights.length;

  const steps = [];
  const dp = Array.from({ length: n + 1 }, () => new Array(capacity + 1).fill(0));

  steps.push({
    line: 4,
    description: `0/1 Knapsack: ${n} items, capacity ${capacity}. Build ${n + 1}x${capacity + 1} DP table.`,
    variables: { n, capacity, weights, values },
    dpTable2D: dp.map(r => [...r]),
    weights,
    values,
    capacity,
    dimensions: '2D',
    currentCell: null
  });

  for (let i = 1; i <= n; i++) {
    const w = weights[i - 1];
    const v = values[i - 1];

    for (let c = 0; c <= capacity; c++) {
      steps.push({
        line: 8,
        description: `Item ${i}: weight=${w}, value=${v}. Consider capacity=${c}`,
        variables: { i, c, weight: w, value: v },
        dpTable2D: dp.map(r => [...r]),
        weights,
        values,
        capacity,
        currentCell: [i, c],
        currentItem: i - 1,
        dimensions: '2D'
      });

      if (w <= c) {
        const take = dp[i - 1][c - w] + v;
        const skip = dp[i - 1][c];
        dp[i][c] = Math.max(take, skip);

        steps.push({
          line: 10,
          description: `Weight ${w} fits. Max(take=${take}, skip=${skip}) = ${dp[i][c]}`,
          variables: { take, skip, chosen: dp[i][c] },
          dpTable2D: dp.map(r => [...r]),
          weights,
          values,
          capacity,
          currentCell: [i, c],
          dependencies: [[i - 1, c - w], [i - 1, c]],
          dimensions: '2D',
          tookItem: take > skip
        });
      } else {
        dp[i][c] = dp[i - 1][c];
        steps.push({
          line: 12,
          description: `Weight ${w} > capacity ${c}. Skip. dp[${i}][${c}] = ${dp[i][c]}`,
          variables: { skipReason: 'weight exceeds capacity' },
          dpTable2D: dp.map(r => [...r]),
          weights,
          values,
          capacity,
          currentCell: [i, c],
          dependencies: [[i - 1, c]],
          dimensions: '2D'
        });
      }
    }
  }

  steps.push({
    line: 16,
    description: `Complete! Maximum value in knapsack = ${dp[n][capacity]}`,
    variables: { result: dp[n][capacity] },
    dpTable2D: dp.map(r => [...r]),
    weights,
    values,
    capacity,
    currentCell: [n, capacity],
    dimensions: '2D',
    result: dp[n][capacity]
  });

  return steps;
};

// ============ GRAPH ALGORITHMS ============

// Predefined graph: 7 nodes with positions
const DEFAULT_GRAPH = {
  nodes: [
    { id: 0, x: 300, y: 60 },
    { id: 1, x: 150, y: 150 },
    { id: 2, x: 450, y: 150 },
    { id: 3, x: 80, y: 260 },
    { id: 4, x: 300, y: 260 },
    { id: 5, x: 520, y: 260 },
    { id: 6, x: 300, y: 350 }
  ],
  edges: [
    [0, 1], [0, 2],
    [1, 3], [1, 4],
    [2, 4], [2, 5],
    [3, 6], [4, 6], [5, 6]
  ],
  weights: {
    '0-1': 2, '0-2': 5,
    '1-3': 4, '1-4': 1,
    '2-4': 3, '2-5': 2,
    '3-6': 3, '4-6': 6, '5-6': 4
  }
};

const buildAdjList = () => {
  const adj = {};
  DEFAULT_GRAPH.nodes.forEach(n => { adj[n.id] = []; });
  DEFAULT_GRAPH.edges.forEach(([a, b]) => {
    adj[a].push(b);
    adj[b].push(a);
  });
  return adj;
};

export const generateBFSSteps = (start) => {
  const steps = [];
  const adj = buildAdjList();
  const visited = new Set();
  const queue = [start];
  const traversal = [];

  steps.push({
    line: 4,
    description: `Starting BFS from node ${start}. Add to queue.`,
    variables: { start },
    graph: DEFAULT_GRAPH,
    visited: [],
    queue: [start],
    traversal: [],
    current: null,
    dataStructure: 'queue'
  });

  while (queue.length > 0) {
    const node = queue.shift();
    if (visited.has(node)) continue;

    visited.add(node);
    traversal.push(node);

    steps.push({
      line: 8,
      description: `Dequeue and visit node ${node}. Traversal: [${traversal.join(', ')}]`,
      variables: { current: node },
      graph: DEFAULT_GRAPH,
      visited: [...visited],
      queue: [...queue],
      traversal: [...traversal],
      current: node,
      dataStructure: 'queue'
    });

    for (const neighbor of adj[node]) {
      if (!visited.has(neighbor) && !queue.includes(neighbor)) {
        queue.push(neighbor);
        steps.push({
          line: 12,
          description: `Discover neighbor ${neighbor}. Add to queue.`,
          variables: { neighbor, from: node },
          graph: DEFAULT_GRAPH,
          visited: [...visited],
          queue: [...queue],
          traversal: [...traversal],
          current: node,
          exploredEdge: [node, neighbor],
          dataStructure: 'queue'
        });
      }
    }
  }

  steps.push({
    line: 16,
    description: `BFS complete! Order: [${traversal.join(' → ')}]`,
    variables: { result: traversal.join(', ') },
    graph: DEFAULT_GRAPH,
    visited: [...visited],
    queue: [],
    traversal: [...traversal],
    current: null,
    dataStructure: 'queue',
    result: traversal.join(', ')
  });

  return steps;
};

export const generateDFSSteps = (start) => {
  const steps = [];
  const adj = buildAdjList();
  const visited = new Set();
  const stack = [start];
  const traversal = [];

  steps.push({
    line: 4,
    description: `Starting DFS from node ${start}. Push to stack.`,
    variables: { start },
    graph: DEFAULT_GRAPH,
    visited: [],
    stack: [start],
    traversal: [],
    current: null,
    dataStructure: 'stack'
  });

  while (stack.length > 0) {
    const node = stack.pop();
    if (visited.has(node)) continue;

    visited.add(node);
    traversal.push(node);

    steps.push({
      line: 8,
      description: `Pop and visit node ${node}. Traversal: [${traversal.join(', ')}]`,
      variables: { current: node },
      graph: DEFAULT_GRAPH,
      visited: [...visited],
      stack: [...stack],
      traversal: [...traversal],
      current: node,
      dataStructure: 'stack'
    });

    // Push neighbors in reverse for consistent order
    const neighbors = [...adj[node]].reverse();
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        stack.push(neighbor);
        steps.push({
          line: 12,
          description: `Push neighbor ${neighbor} to stack.`,
          variables: { neighbor, from: node },
          graph: DEFAULT_GRAPH,
          visited: [...visited],
          stack: [...stack],
          traversal: [...traversal],
          current: node,
          exploredEdge: [node, neighbor],
          dataStructure: 'stack'
        });
      }
    }
  }

  steps.push({
    line: 16,
    description: `DFS complete! Order: [${traversal.join(' → ')}]`,
    variables: { result: traversal.join(', ') },
    graph: DEFAULT_GRAPH,
    visited: [...visited],
    stack: [],
    traversal: [...traversal],
    current: null,
    dataStructure: 'stack',
    result: traversal.join(', ')
  });

  return steps;
};

export const generateDijkstraSteps = (start) => {
  const steps = [];
  const n = DEFAULT_GRAPH.nodes.length;
  const dist = {};
  const visited = new Set();

  DEFAULT_GRAPH.nodes.forEach(node => {
    dist[node.id] = node.id === start ? 0 : Infinity;
  });

  const getWeight = (a, b) => {
    return DEFAULT_GRAPH.weights[`${Math.min(a, b)}-${Math.max(a, b)}`];
  };

  const adj = buildAdjList();

  steps.push({
    line: 5,
    description: `Dijkstra from node ${start}. All distances set to Infinity except source (0).`,
    variables: { start },
    graph: DEFAULT_GRAPH,
    visited: [],
    distances: { ...dist },
    current: null
  });

  for (let iter = 0; iter < n; iter++) {
    // Find unvisited node with min distance
    let u = -1;
    let minDist = Infinity;
    for (const node of DEFAULT_GRAPH.nodes) {
      if (!visited.has(node.id) && dist[node.id] < minDist) {
        minDist = dist[node.id];
        u = node.id;
      }
    }

    if (u === -1) break;

    visited.add(u);
    steps.push({
      line: 10,
      description: `Pick node ${u} with min distance ${dist[u]}. Mark as visited.`,
      variables: { current: u, distance: dist[u] },
      graph: DEFAULT_GRAPH,
      visited: [...visited],
      distances: { ...dist },
      current: u
    });

    for (const v of adj[u]) {
      if (visited.has(v)) continue;
      const weight = getWeight(u, v);
      const newDist = dist[u] + weight;

      steps.push({
        line: 14,
        description: `Check edge ${u} → ${v} (weight ${weight}). New dist = ${dist[u]} + ${weight} = ${newDist}`,
        variables: { u, v, weight, newDist, oldDist: dist[v] === Infinity ? '∞' : dist[v] },
        graph: DEFAULT_GRAPH,
        visited: [...visited],
        distances: { ...dist },
        current: u,
        exploredEdge: [u, v]
      });

      if (newDist < dist[v]) {
        dist[v] = newDist;
        steps.push({
          line: 15,
          description: `Update dist[${v}] = ${newDist} (was ${dist[v] === newDist ? '∞' : 'greater'})`,
          variables: { v, newDist },
          graph: DEFAULT_GRAPH,
          visited: [...visited],
          distances: { ...dist },
          current: u,
          exploredEdge: [u, v],
          updated: v
        });
      }
    }
  }

  const finalDist = Object.entries(dist)
    .map(([k, v]) => `${k}:${v === Infinity ? '∞' : v}`)
    .join(', ');

  steps.push({
    line: 20,
    description: `Complete! Shortest distances: {${finalDist}}`,
    variables: { finalDistances: finalDist },
    graph: DEFAULT_GRAPH,
    visited: [...visited],
    distances: { ...dist },
    current: null,
    result: finalDist
  });

  return steps;
};
